import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiFetch } from '../lib/api'

interface User {
  id: string
  email: string
  name?: string
  trade?: string
  subscription_tier: string
  verified: boolean
  cscs_status: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  sendMagicLink: (email: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkSession()
  }, [])

  async function checkSession() {
    const token = localStorage.getItem('digsafe_token')
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const data = await apiFetch('/api/worker/profile')
      setUser(data.worker)
    } catch {
      localStorage.removeItem('digsafe_token')
    }
    setLoading(false)
  }

  async function sendMagicLink(email: string) {
    await apiFetch('/api/auth/send-magic-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  function logout() {
    localStorage.removeItem('digsafe_token')
    setUser(null)
  }

  async function refreshUser() {
    try {
      const data = await apiFetch('/api/worker/profile')
      setUser(data.worker)
    } catch {}
  }

  return (
    <AuthContext.Provider value={{ user, loading, sendMagicLink, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
